import InputText from "@/components/common/InputText";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import z from "zod";
import InputNumber from "@/components/common/InputNumber";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import ThreeDoteIcon from "../../../../assets/svg/ThreeDotsVertical.svg";
import TrashCan from "../../../../assets/svg/TrashCan.svg";
import NewEditIcon from "../../../../assets/svg/NewEditIcon.svg";
import { updateKanbanSchema } from "@backend/src/schemas/projectSchema";
import useKanbanColumnMutation, {
  KanbanColumnType,
} from "@/api/mutation/useKanbanCreateColumn";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import useAllKanbanColumnQuery from "@/api/query/useAllKanbanColumn";
import Dialog from "@/components/common/Dialog";
import useRemoveKanbanColumnsMutation from "@/api/mutation/useKanbanColumnRemove";
import useKanbanUpdateColumnMutation from "@/api/mutation/useKanbanUpdateColumn";
import RulesForm from "./rulesForm";
import ErrorMessage from "@/components/common/ErrorMessage";
import { errorConfig } from "@/lib/toastError";
import { useTranslation } from "react-i18next";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import FormLabel from "@/components/common/FormLabel";
import KanbanSetting from "@/assets/v2/svg/KanbanSetting.svg";
import useProjectDetail from "@/api/query/useProjectDetailQuery";
import { UserRoleEnumValue } from "@backend/src/schemas/enums";
import { useUser } from "@/hooks/useUser";
type Props = {
  setColumes: (column: KanbanColumnType[]) => void;
  close: () => void;
  isProjectEditBlocked?: boolean
  setIsProjectEditBlockedDialogOpen?: (data: boolean) => void;
};
function RulesSetups(props: Props) {
  const { t } = useTranslation();

  const { projectId } = useParams();
  const projectQuery = useProjectDetail(projectId, true);
  const allKanbanColumn = useAllKanbanColumnQuery(projectId);
  const removeKanbanColumnsMutation = useRemoveKanbanColumnsMutation();
  const kanbanUpdateColumnMutation = useKanbanUpdateColumnMutation();
  const [rules, setRules] = useState<KanbanColumnType[]>([]);
  const [ruleEditData, setRuleEditData] = useState<KanbanColumnType>();
  const [ruleRemoveId, setRuleRemoveId] = useState<string>();
  const [roleInProject, setRoleInProject] =
    useState<keyof typeof UserRoleEnumValue>();
  const { user } = useUser();

  const updateKanbanColumnFormik = useFormik<
    z.infer<typeof updateKanbanSchema>
  >({
    initialValues: {
      name: ruleEditData?.name || "",
      percentage: ruleEditData?.percentage || 0,
    },
    validationSchema: toFormikValidationSchema(updateKanbanSchema),
    onSubmit: (values) => {
      const data = {
        ...values,
        id: ruleEditData?.kanbanColumnId,
      };
      if (setPercentage(values.percentage, values.name)) {
        if (typeof values.percentage == "string") {
          data.percentage = null;
        }
        kanbanUpdateColumnMutation.mutate(data, {
          onSuccess() {
            updateKanbanColumnFormik.setValues({
              name: "",
              percentage: 0,
            });
            setRuleEditData(undefined);
            allKanbanColumn.refetch();
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message + ""),
              errorConfig,
            );
          },
        });
      }
    },
  });
  const handleEdit = (data: KanbanColumnType) => {
    setRuleEditData(data);
    updateKanbanColumnFormik.setValues({
      name: data.name,
      percentage: data.percentage,
    });
  };
  const handleRemove = (id: string) => {
    setRuleRemoveId(id);
  };
  const kanbanColumnMutation = useKanbanColumnMutation(projectId);
  useEffect(() => {
    if (allKanbanColumn.data?.data.data) {
      if (allKanbanColumn.data?.data.data.length > 0) {
        allKanbanColumn.data?.data.data.sort(
          (a, b) => (a.percentage ?? 0) - (b.percentage ?? 0),
        );
      }
      setRules(allKanbanColumn.data?.data.data);
      props.setColumes(allKanbanColumn.data?.data.data);
    }
    if (
      allKanbanColumn.isFetched &&
      allKanbanColumn.data?.data.data.length == 0
    ) {
      kanbanColumnMutation.mutate(
        { name: "Backlog", percentage: null },
        {
          onSuccess() {
            updateKanbanColumnFormik.setValues({
              name: "",
              percentage: 0,
            });
            setRuleEditData(undefined);
            allKanbanColumn.refetch();
          },
          onError(err) {
            toast.error(
              errorKeyFormate(err.response?.data.message + ""),
              errorConfig,
            );
          },
        },
      );
    }
  }, [allKanbanColumn.data?.data.data]);

  useEffect(() => {
    if (projectQuery.data?.data.data) {
      const role = projectQuery.data.data.data.assignedUsers.find(
        (u) => u.user.userId == user?.userId
      )?.projectRole;
      if (role) {
        setRoleInProject(role as keyof typeof UserRoleEnumValue);
      }
    }
  }, [projectQuery.data?.data.data, projectId]);

  const remove = () => {
    removeKanbanColumnsMutation.mutate(String(ruleRemoveId), {
      onSuccess() {
        setRuleRemoveId("");
        allKanbanColumn.refetch();
      },
      onError(err) {
        toast.error(
          errorKeyFormate(err.response?.data.message + ""),
          errorConfig,
        );
      },
    });
  };

  const close = () => {
    props.close();
  };
  const setPercentage = (
    value: number | undefined | null,
    name?: string | undefined | null,
  ) => {
    if (!value) {
      return true;
    } else if (
      rules?.some((d) => d.percentage === Number(value) && d.name == name)
    ) {
      updateKanbanColumnFormik.setErrors({
        percentage: "Rule already exists. Choose a unique one.",
      });
      return false;
    } else {
      return true;
    }
  };
  return (
    <>
      <div className="w-[97%] h-[98%] flex flex-col p-4 border rounded-xl border-gray-300/30 z-50">
        <div className="flex justify-between mt-1 ">
          <div className=" flex gap-2 items-start text-[#0D062D] mb-5">
            {" "}
            <img src={KanbanSetting} alt="" className="w-5 min-w-5 mt-2" />
            <div className="text-2xl">
              {t("create_kanban_rules")}{" "}
              <div className="text-xs text-gray-500">{t("Define your workflow stages and set capacity limits.")}</div>
            </div>
          </div>
          <Button
            onClick={() => close()}
            className=" w-auto md:w-40"
            disabled={rules.length <= 0}
            variant={"primary"}
          >
            {t("go_to_kanban")}
          </Button>
        </div>

        <div className="@container grow overflow-y-auto">
          <div className="grid  @3xl:grid-cols-5 @2xl:grid-cols-4 @xl:grid-cols-3 @md:grid-cols-2 @sm:grid-cols-1 gap-3">
            {rules.map((r, index) => {
              return (
                <div
                  key={index}
                  className="border border-[#F2F2F2] rounded-2xl flex flex-col items-start justify-start px-2.5 py-2 "
                >
                  <div className="flex p-2 w-full ">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        className="w-full"
                        disabled={r.name == "Backlog" || roleInProject === UserRoleEnumValue.TEAM_MEMBER}
                      >
                        <div className="flex justify-between items-center">
                          <div className="text-lg font-semibold">{r.name}</div>
                          <img src={ThreeDoteIcon} className="h-4 w-4" />
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="" align="end">
                        <DropdownMenuSeparator className="mx-1" />
                        <DropdownMenuItem
                          onClick={() => handleRemove(r.kanbanColumnId)}
                          className="flex items-center gap-2"
                        >
                          <img src={TrashCan} />
                          <span className="p-0 font-normal h-auto">
                            {t("remove")}
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleEdit(r)}
                          className="flex items-center gap-2"
                        >
                          <img src={NewEditIcon} className="invert" />
                          <span className="p-0 font-normal h-auto">
                            {t("edit")}
                          </span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="mt-1">
                    <FormLabel className="text-sm  !text-black mb-2">
                      {t("Column Name")}
                    </FormLabel>
                    <InputText
                      disabled={
                        ruleEditData?.kanbanColumnId !== r.kanbanColumnId
                      }
                      value={
                        ruleEditData?.kanbanColumnId == r.kanbanColumnId
                          ? updateKanbanColumnFormik.values.name
                          : r.name
                      }
                      name="name"
                      placeholder={t("column_name")}
                      className="h-8 text-sm rounded-md mt-0 bg-[#FFFFFF]"
                      onChange={updateKanbanColumnFormik.handleChange}
                    />
                    {ruleEditData?.kanbanColumnId === r.kanbanColumnId && (
                      <ErrorMessage>
                        {updateKanbanColumnFormik.errors.name}
                      </ErrorMessage>
                    )}
                  </div>
                  <div className="mt-1 mb-2 w-full">
                    <FormLabel className="text-sm  !text-black mb-2">
                      {t("Task Percentage")}
                    </FormLabel>
                    <InputNumber
                      disabled={
                        ruleEditData?.kanbanColumnId !== r.kanbanColumnId
                      }
                      value={
                        ruleEditData?.kanbanColumnId == r.kanbanColumnId
                          ? (updateKanbanColumnFormik.values.percentage ?? "")
                          : (r.percentage ?? "")
                      }
                      className="h-8 w-full  text-sm rounded-md mt-0 bg-[#FFFFFF]"
                      name="percentage"
                      placeholder={t("task_percentage")}
                      min={0}
                      max={100}
                      onChange={(e) => {
                        const val = e.target.value;
                        const numVal = val === "" ? "" : Number(val);
                        updateKanbanColumnFormik.setFieldValue("percentage", numVal);
                      }}
                    />
                    {ruleEditData?.kanbanColumnId == r.kanbanColumnId && (
                      <ErrorMessage>
                        {updateKanbanColumnFormik.errors.percentage}
                      </ErrorMessage>
                    )}
                  </div>
                  {ruleEditData?.kanbanColumnId == r.kanbanColumnId && (
                    <div className="flex justify-between mt-3">
                      <Button
                        variant={"outline"}
                        size={"sm"}
                        onClick={() => setRuleEditData(undefined)}
                      >
                        {t("cancel")}
                      </Button>
                      <Button
                        onClick={() => {
                          updateKanbanColumnFormik.submitForm();
                        }}
                        variant={"primary_outline"}
                        size={"sm"}
                      >
                        {t("update")}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            <RulesForm
              projectId={projectId ?? ""}
              refetch={() => allKanbanColumn.refetch()}
              rules={rules}
              isProjectEditBlocked={props.isProjectEditBlocked}
              setIsProjectEditBlockedDialogOpen={props.setIsProjectEditBlockedDialogOpen}
              disabled={user?.userOrganisation[0]?.role == UserRoleEnumValue.ADMINISTRATOR ? false : roleInProject === UserRoleEnumValue.TEAM_MEMBER}
              className=" border rounded-2xl border-dashed border-secondary/50 !p-3"
            />
          </div>
        </div>
        {/* <div className="flex justify-end mt-4">
          <Button
            onClick={() => close()}
            disabled={rules.length <= 0}
            variant={"primary"}
          >
            Go To Kanban
          </Button>
        </div> */}
      </div>
      <Dialog
        isOpen={Boolean(ruleRemoveId)}
        onClose={() => { }}
        modalClass="rounded-lg"
      >
        <div className="flex flex-col gap-2 p-6 ">
          <img src={TrashCan} className="w-12 m-auto" />{" "}
          {t("common_delete_message")}
          <div className="flex gap-2 ml-auto">
            <Button
              variant={"outline"}
              isLoading={removeKanbanColumnsMutation.isPending}
              disabled={removeKanbanColumnsMutation.isPending}
              onClick={() => setRuleRemoveId("")}
            >
              Cancel
            </Button>
            <Button variant={"primary"} onClick={remove}>
              Delete
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default RulesSetups;
